import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'
import { AddTaskModalComponent } from '@app/features/scope-overview/components/scope-tab/add-task-modal/add-task-modal.component'
import { AuthService } from '@core/service/auth.service'
import { BootstrapActions } from '@core/store'
import { CompanyManagementActions } from '@app/features/company-management/store/actions/company-management.actions'
import { combineLatest, map, Observable, of } from 'rxjs'
import { CompanyManagementSelectors } from '@app/features/company-management/store/selectors/company-management.selectors'
import { Component, EventEmitter, Output, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { ModalConfig } from '@core/model/modal-config.model'
import { NewTask } from '@core/model/new-task.model'
import { Page } from '@app/features/scoping/models/page.model'
import { PageEvent } from '@angular/material/paginator'
import { Pagination } from '@core/model/definitions/pagination.interface'
import { Preference } from '@core/model/user-preferences.interface'
import { Privilege } from '@core/model/enums/privilege.enum'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { Sort } from '@angular/material/sort'
import { SortDirection } from '@app/core/model/enums/sort-direction.enum'
import { Store } from '@ngrx/store'
import { TableColumnKey } from '@app/shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { UploadType } from '@app/features/company-management/models/upload-modal-config'
import { User } from '@core/model/user.model'
import { UploadModalComponent } from '@shared/components/upload-modal/upload-modal.component'
import { FormControl } from '@angular/forms'
import { trackById } from '@shared/utils/utils'
import {
  AddLibraryTaskRolesModalComponent,
  AddLibraryTaskRolesModalConfig,
} from '@app/features/company-management/components/add-library-task-roles-modal/add-library-task-roles-modal.component'
import { cloneDeep } from 'lodash'
import { Deliverable, LibraryTaskTemplate } from '@app/features/scoping/models/deliverable.model'
import { MappedEntity } from '@app/features/scoping/models/mapped-entity.model'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { Title } from '@angular/platform-browser'
import { DescriptionUpdate } from '@shared/components/description-editor/description-editor.component'

@Component({ selector: 'tasks', templateUrl: './tasks.component.html', styleUrls: ['./tasks.component.scss'] })
export class TasksComponent implements OnInit {
  loggedInUser!: User
  currentTab = 'Tasks'
  showDescriptions: boolean
  outOfSync: boolean
  taskColumns$!: Observable<Preference[]>
  menuOptions!: MenuOptions[]
  loading$: Observable<boolean>
  tasks$: Observable<Page<Deliverable>>
  mappedTasksDataSource$!: Observable<MappedEntity<Deliverable>[]>
  tableData: Pagination
  searchText = ''
  searchRoleText = ''
  page = 0
  sort: Sort = { active: TableColumnKey.NAME, direction: SortDirection.ASC }
  rateCards: any[]
  tableExpanded = true
  currentTask$: Observable<LibraryTaskTemplate>
  currentTask: LibraryTaskTemplate
  editName: boolean
  @Output() onRolesSearch = new EventEmitter<any>()

  constructor(
    private authService: AuthService,
    private manageService: CompanyManagementService,
    private mappingService: DataMappingService,
    private store: Store,
    private dialog: MatDialog,
    private titleService: Title,
    private snackbarService: SnackbarService
  ) {
    this.loggedInUser = this.authService.loggedInUser
    this.tasks$ = this.store.select(CompanyManagementSelectors.selectTasks)
    this.taskColumns$ = this.store.select(CompanyManagementSelectors.selectTaskColumns)
    this.loading$ = this.store.select(CompanyManagementSelectors.selectLoadingTasks)
    this.currentTask$ = this.store.select(CompanyManagementSelectors.selectCurrentTask)
  }

  getLibraryTaskTemplates() {
    this.mappedTasksDataSource$ = combineLatest([this.tasks$, this.taskColumns$]).pipe(
      map(([tasks, userPreferences]) => {
        this.tableData = { totalCount: tasks.totalElements, page: tasks.number }
        return this.mappingService.transformArray<Deliverable>(tasks.content, userPreferences)
      })
    )
  }

  ngOnInit() {
    this.titleService.setTitle('Tasks | Scope')
    this.getLibraryTaskTemplates()
    this.store.dispatch(CompanyManagementActions.getTasks({ page: 0, searchText: this.searchText, sort: this.sort }))
    this.store.dispatch(BootstrapActions.loadTaskPreferences())
    this.menuOptions = [
      {
        callback: (element: any) => {
          this.onSelectTask(element.entity)
        },
        name: () => 'Edit',
        icon: () => 'edit',
        hasPrivilege: () => Privilege.LIBRARY_DELIVERABLE__EDIT,
      },
      {
        callback: (element: any) => this.deleteTask(element.entity),
        name: () => 'Delete',
        icon: () => 'delete',
        hasPrivilege: () => Privilege.LIBRARY_DELIVERABLE__DELETE,
      },
    ]
  }

  onSearchKeyPressed(value: string) {
    this.searchText = value
    this.page = 0
    this.store.dispatch(
      CompanyManagementActions.getTasks({ page: this.page, searchText: this.searchText, sort: this.sort })
    )
  }

  addTask() {
    const dialog = this.dialog.open(AddTaskModalComponent)
    dialog.componentInstance.newTaskTemplate = true
    dialog.componentInstance.rateCards$ = this.manageService.getCompanyRateCards()
    dialog.componentInstance.onSubmit.subscribe((task: NewTask) => {
      dialog.close()
      this.manageService.addLibraryTaskTemplate(task).subscribe({
        next: (response: LibraryTaskTemplate) => {
          this.page = 0
          this.store.dispatch(
            CompanyManagementActions.getTasks({ page: this.page, searchText: this.searchText, sort: this.sort })
          )
          this.getTask(response.id, response.rateCardVersion.id)
        },
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          return error
        },
      })
    })
  }

  getTask(taskId: number, rateCardVersionId: number) {
    this.store.dispatch(CompanyManagementActions.getLibraryTask({ taskId, rateCardVersionId }))
    this.currentTask$.subscribe((task) => {
      this.currentTask = task
      if (this.currentTask != null) {
        this.titleService.setTitle(`${this.currentTask.name} | Tasks | SCOPE`)
      }
    })
  }

  deleteTask(task) {
    const body = `Are you sure you want to delete "` + task.name + `"?`
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete "` + task.name + `"`,
        body,
        'Delete',
        undefined,
        () => {
          dialog.close()
          this.manageService.deleteLibraryTaskTemplate(task.id).subscribe({
            next: () => {
              this.store.dispatch(
                CompanyManagementActions.getTasks({ page: this.page, searchText: this.searchText, sort: this.sort })
              )
            },
            error: (error) => {
              this.snackbarService.showDefaultErrorSnackbar()
              return error
            },
          })
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  onChangePage(pageEvent: PageEvent) {
    this.page = pageEvent.pageIndex
    this.store.dispatch(
      CompanyManagementActions.getTasks({ page: this.page, searchText: this.searchText, sort: this.sort })
    )
  }

  onSort(sort: Sort) {
    this.sort = sort
    this.store.dispatch(
      CompanyManagementActions.getTasks({ page: this.page, searchText: this.searchText, sort: this.sort })
    )
  }

  onTogglePreference(preference: Preference) {
    this.store.dispatch(BootstrapActions.updateTaskColumnPreferences({ preference: preference }))
  }

  onSelectTask(item: any) {
    if (item.entity) {
      this.getTask(item.entity.id, item.entity.rateCardVersion.id)
    } else {
      this.getTask(item.id, item.rateCardVersion.id)
    }
  }

  upload() {
    const dialog = this.dialog.open(UploadModalComponent, {
      data: {
        title: 'Upload Tasks',
        body: `Upload Tasks using Excel file`,
        type: UploadType.TASK,
        verifyService: this.manageService,
        submitFn: (file: File) => this.manageService.import(file, UploadType.TASK),
        successFn: () => {
          dialog.close()
          this.snackbarService.showSnackbar(
            `File was successfully uploaded. Please, refresh the page.`,
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.SUCCESS
          )
        },
        showBody: true,
        rateCards$: this.manageService.getCompanyRateCards(),
      },
    })
  }

  export() {
    if (!this.rateCards) {
      this.manageService.getCompanyRateCards().subscribe({
        next: (rateCards) => {
          this.rateCards = [{ id: 0, name: 'All Rate Cards' }].concat(rateCards)
          this.openExportModal()
        },
      })
    } else {
      this.openExportModal()
    }
  }

  openExportModal() {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Export Tasks`,
        `Choose the rate card to download tasks for, or select "All Rate Cards"`,
        `Download`,
        undefined,
        (form: FormControl, selections: any[]) => {
          dialog.close()
          const rateCard = selections[0]
          this.manageService.exportTasks(rateCard?.rateCardIdentity?.id).subscribe({
            next: (result: any) => {
              if (!result.resolved) {
                this.snackbarService.showSnackbar(
                  'Export started and when complete file will automatically download',
                  SNACKBAR_LENGTH_LONG,
                  SnackbarEventType.INFO
                )
              }
            },
            error: () => {
              this.snackbarService.showSnackbar(
                'Failed to download export',
                SNACKBAR_LENGTH_LONG,
                SnackbarEventType.ERROR
              )
            },
          })
        },
        undefined,
        [
          {
            name: 'rateCard',
            control: new FormControl(),
            type: 'autocomplete',
            required: true,
            label: `Choose the rate card`,
            options$: of(this.rateCards),
          },
        ],
        false,
        true,
        undefined,
        (form: FormControl, selections: any[]) => {
          return !!selections.length
        }
      ),
    })
  }

  updateTask = (task = null) => {
    this.store.dispatch(CompanyManagementActions.updateLibraryTask({ task: task }))
    this.outOfSync = false
  }

  renameTask = (newName: string) => {
    const task = cloneDeep(this.currentTask)
    this.updateTask({ ...task, name: newName })
  }

  updateTaskField = (key: string, value: string) => {
    const task = cloneDeep(this.currentTask)
    this.updateTask({ ...task, [key]: value })
  }

  updateExtraDescription = ($event: DescriptionUpdate) => {
    const task = cloneDeep(this.currentTask)
    this.updateTask({ ...task, description: $event.main, extraDescription: $event.extra })
  }

  backToTasks = () => {
    this.currentTask = null
    if (!this.outOfSync) {
      this.store.dispatch(CompanyManagementActions.resetLibraryTask())
    }
    this.getLibraryTaskTemplates()
    this.store.dispatch(CompanyManagementActions.getTasks({ page: 0, searchText: this.searchText, sort: this.sort }))
    this.store.dispatch(BootstrapActions.loadTaskPreferences())
  }

  addRoles(_task: LibraryTaskTemplate) {
    const dialogConfig: AddLibraryTaskRolesModalConfig = {
      title: `Add/Update Role(s)`,
      rateCard: this.currentTask.rateCardVersion,
      task: this.currentTask,
    }

    this.dialog.open(AddLibraryTaskRolesModalComponent, { data: dialogConfig })
  }

  onRoleSearch(value: string) {
    this.searchRoleText = value
  }

  protected readonly trackById = trackById
}
